import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { InstallBanner } from '../components/InstallBanner';
import { Mail, Lock, LogIn, Loader2 } from 'lucide-react';
import SocialAuthButtons from '../components/SocialAuthButtons';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(email, password);
        
        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.error);
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div 
                className="hidden lg:flex lg:w-1/2 items-center justify-center p-12"
                style={{
                    backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/ad72222b-8ef5-4b27-9c5b-21debe554c06/images/40d10c6b1137089e0d3a8ddda2794d0d0abb2fe4ac2a6b914adbeb630e7a8628.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="brutalist-card p-8 max-w-md">
                    <h2 className="text-3xl font-black tracking-tight mb-4">A-Level AI Grader</h2>
                    <p className="text-lg text-zinc-600">Get instant, detailed feedback on your exam answers. Powered by AI, designed for Pearson Edexcel students.</p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F8F7FC]">
                <div className="w-full max-w-md">
                    <InstallBanner />
                    <div className="mb-8">
                        <h1 className="text-4xl font-black tracking-tight text-zinc-950 mb-2">Welcome back</h1>
                        <p className="text-zinc-600">Sign in to continue to your dashboard</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="brutalist-card p-4 bg-red-50 border-red-500" data-testid="login-error">
                                <p className="text-red-700 font-medium">{error}</p>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="brutalist-input has-leading-icon"
                                    placeholder="you@example.com"
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-semibold text-zinc-700">
                                    Password
                                </label>
                                <Link 
                                    to="/forgot-password" 
                                    className="text-sm text-zinc-600 hover:text-zinc-950 underline"
                                    data-testid="forgot-password-link"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="brutalist-input has-leading-icon"
                                    placeholder="••••••••"
                                    required
                                    data-testid="login-password-input"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="brutalist-btn-primary w-full flex items-center justify-center gap-2"
                            data-testid="login-submit-button"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                <>
                                    <LogIn className="w-5 h-5" />
                                    Sign In
                                </>
                            )}
                        </button>
                    </form>

                    <SocialAuthButtons mode="signin" />

                    <div className="mt-8 text-center">
                        <p className="text-zinc-600">
                            Don't have an account?{' '}
                            <Link to="/register" className="font-semibold text-zinc-950 underline hover:no-underline" data-testid="register-link">
                                Create one
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
